import { IHeroVital } from "@/store/apis/heroAPI/hero.types"
import { IVitalView } from "@/types/vitals.types"

const MS_PER_SECOND = 1000

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

const elapsedSeconds = (asOf: string, nowMs: number): number =>
  Math.max(0, (nowMs - Date.parse(asOf)) / MS_PER_SECOND)

/**
 * Returns `fallbackNowMs` (the caller's `ServerDate.now()`) when `serverTime` does not parse.
 */
export const serverNowMs = (
  serverTime: string,
  receivedAtMs: number,
  clientNowMs: number,
  fallbackNowMs: number
): number => {
  const serverMs = Date.parse(serverTime)
  return Number.isNaN(serverMs)
    ? fallbackNowMs
    : clientNowMs + (serverMs - receivedAtMs)
}

export const projectVital = (vital: IHeroVital, nowMs: number): number =>
  Math.min(
    vital.max,
    vital.current +
      Math.floor(vital.regenPerSecond * elapsedSeconds(vital.asOf, nowMs))
  )

export const nextChangeMs = (
  vitals: readonly IHeroVital[],
  nowMs: number
): number | null => {
  let earliest: number | null = null
  for (const vital of vitals) {
    if (
      !(vital.regenPerSecond > 0) ||
      !(projectVital(vital, nowMs) < vital.max)
    ) {
      continue
    }
    const nextPoint =
      Math.floor(vital.regenPerSecond * elapsedSeconds(vital.asOf, nowMs)) + 1
    const changeAt =
      Date.parse(vital.asOf) +
      Math.ceil((nextPoint / vital.regenPerSecond) * MS_PER_SECOND)
    if (earliest === null || changeAt < earliest) {
      earliest = changeAt
    }
  }
  return earliest
}

export const vitalFill = (current: number, max: number): number =>
  max > 0 ? clamp01(current / max) : 0

export const buildVitalViews = (
  vitals: readonly IHeroVital[],
  nowMs: number
): IVitalView[] =>
  vitals.map((vital) => {
    const current = projectVital(vital, nowMs)
    return {
      code: vital.code,
      current,
      max: vital.max,
      fill: vitalFill(current, vital.max)
    }
  })

export const vitalViewsEqual = (
  a: readonly IVitalView[],
  b: readonly IVitalView[]
): boolean =>
  a.length === b.length &&
  a.every(
    (view, index) =>
      view.code === b[index].code &&
      view.current === b[index].current &&
      view.max === b[index].max
  )
